/**
 * PodGuard — restricts access to POD dashboard areas (webinars, resources,
 * AI templates, roundtable, success). Only super_admin, pod_subscriber, and
 * accounts explicitly granted `pod_access` by a super_admin may pass through.
 *
 * The /pod marketing landing page is public — no guard there.
 */
import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Lock } from "@phosphor-icons/react";

export default function PodGuard({ children }) {
  const { user, loading } = useAuth();
  const [decided, setDecided] = useState(false);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { setAllowed(false); setDecided(true); return; }
    const role = user.role;
    const ok = role === "super_admin" || role === "pod_subscriber" || user.pod_access === true || user.can_access_pod === true;
    setAllowed(ok);
    setDecided(true);
  }, [user, loading]);

  if (loading || !decided) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Checking POD access…</div>;
  if (!user) return <Navigate to="/login?next=/pod" replace />;
  if (!allowed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="pod-access-denied">
        <div className="max-w-md bg-white border border-amber-200 p-8 text-center">
          <Lock size={42} weight="fill" className="text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>POD subscription required</h1>
          <p className="text-sm text-slate-600 mb-6">The SBL Leadership POD is a separate professional community. Your current account is not subscribed.</p>
          <div className="flex flex-col gap-2">
            <Link to="/pod/signup"><Button className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="pod-go-signup">Join the POD</Button></Link>
            <Link to="/pod"><Button variant="outline" className="w-full rounded-none">Read about the POD</Button></Link>
            <Link to="/dashboard"><Button variant="ghost" className="w-full rounded-none text-xs text-slate-500">Back to dashboard</Button></Link>
          </div>
        </div>
      </div>
    );
  }
  return children;
}
